import React, {
  useContext,
  useEffect,
  useReducer,
  useState,
  useRef,
} from 'react';

import Collapse from 'react-bootstrap/Collapse';
import { Link, NavLink } from 'react-router-dom';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { MenuList } from './Menu';
import { ThemeContext } from '../../../context/ThemeContext';
import { useLoggedInUserContextProvider } from '../../../context/LoggedInUserContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useChairReportStatuses } from '../../../context/ChairReportStatusesContext';

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: '',
  activeSubmenu: '',
};

const handleMailto = (email) => {
  window.open(`mailto:${email}`, '_blank');
};

const SideBar = () => {
  const { userDetails } = useLoggedInUserContextProvider();

  const { iconHover, sidebarposition, headerposition, sidebarLayout } =
    useContext(ThemeContext);

  const [state, setState] = useReducer(reducer, initialState);
  const [sidebarHeight, setSidebarHeight] = useState('100vh');

  const heartRef = useRef(null);
  const sidebarRef = useRef(null);

  const { isCouncilMember } = useUserPermissions();
  const { allReportsSubmitted: councilMemberCanViewRankingReport } =
    useChairReportStatuses() || {};

  function heartBlast() {
    if (heartRef.current) {
      return heartRef.current.classList.toggle('heart-blast');
    }
  }

  useEffect(() => {
    const calculateSidebarHeight = () => {
      if (sidebarRef.current) {
        const rect = sidebarRef.current.getBoundingClientRect();
        const availableHeight = window.innerHeight - rect.top;
        setSidebarHeight(`${availableHeight}px`);
      }
    };

    calculateSidebarHeight();

    const handleResize = () => {
      calculateSidebarHeight();
    };

    window.addEventListener('resize', handleResize);

    const timeoutId = setTimeout(calculateSidebarHeight, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const [hideOnScroll, setHideOnScroll] = useState(true);
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y;
      if (isShow !== hideOnScroll) setHideOnScroll(isShow);
    },
    [hideOnScroll],
  );

  const handleMenuActive = (menuTitle, hasChildren) => {
    if (hasChildren) {
      setState({
        active: state.active === menuTitle ? '' : menuTitle,
        activeSubmenu: '',
      });
    } else {
      setState({
        active: state.active === menuTitle ? '' : menuTitle,
        activeSubmenu: '',
      });
    }
  };

  const handleSubmenuActive = (submenuTitle, parentTitle) => {
    setState({
      activeSubmenu: state.activeSubmenu === submenuTitle ? '' : submenuTitle,
      active: parentTitle,
    });
  };

  const getSidebarClass = () => {
    if (
      sidebarposition.value === 'fixed' &&
      sidebarLayout.value === 'horizontal' &&
      headerposition.value === 'static'
    ) {
      return hideOnScroll > 120 ? 'fixed' : '';
    }
    return '';
  };

  const menuItems = MenuList(handleMailto, councilMemberCanViewRankingReport);

  return (
    <div
      ref={sidebarRef}
      className={`dlabnav sidebar-container border-right ${iconHover} ${getSidebarClass()}`}
      style={{ height: sidebarHeight }}
    >
      <div className="sidebar-content">
        <ul className="metismenu" id="menu">
          <li className="menu-title" />
          {userDetails &&
            menuItems.map((data, index) => {
              const listItemClass =
                data.content && data.content.length > 0 ? '' : 'no-over-flow';
              const hasAccess =
                Array.isArray(userDetails.roles) &&
                data.access.some((role) => userDetails.roles.includes(role));

              if (!hasAccess) {
                return null;
              }

              const menuClass = data.classChange;
              if (menuClass === 'menu-title') {
                return (
                  <li className={menuClass} key={index}>
                    {data.title}
                  </li>
                );
              }
              return (
                <li
                  className={`${
                    state.active === data.title
                      ? 'mm-active'
                      : `${listItemClass}`
                  }`}
                  key={index}
                >
                  {data.content && data.content.length > 0 ? (
                    <>
                      <Link
                        to="#"
                        className="has-arrow"
                        onClick={() => {
                          handleMenuActive(data.title);
                        }}
                      >
                        <i className={data.iconStyle} />
                        <span className="nav-text">{data.title}</span>
                      </Link>
                      <Collapse in={state.active === data.title}>
                        <ul
                          className={`${
                            menuClass === 'mm-collapse' ? 'mm-show' : ''
                          }`}
                        >
                          {data.content &&
                            data.content.map((submenu, dataIndex) => (
                              <li
                                key={dataIndex}
                                className={`${
                                  state.activeSubmenu === submenu.title
                                    ? 'active-submenu-item'
                                    : ''
                                }`}
                              >
                                {submenu.custom && submenu.render()}

                                {!submenu.custom &&
                                  submenu.content &&
                                  submenu.content.length > 0 && (
                                    <NavLink
                                      to={submenu.to}
                                      className={({ isActive }) =>
                                        `dropdown-item ${submenu.hasMenu ? 'has-arrow' : ''} ${
                                          isActive ? 'active-submenu-item' : ''
                                        }`
                                      }
                                      onClick={() =>
                                        handleSubmenuActive(
                                          submenu.title,
                                          data.title,
                                        )
                                      }
                                    >
                                      {submenu.title}
                                    </NavLink>
                                  )}

                                {!submenu.custom &&
                                  (!submenu.content ||
                                    submenu.content.length === 0) && (
                                    <Link
                                      to={submenu.to}
                                      onClick={() =>
                                        handleSubmenuActive(
                                          submenu.title,
                                          data.title,
                                        )
                                      }
                                    >
                                      {submenu.title}
                                    </Link>
                                  )}
                              </li>
                            ))}
                        </ul>
                      </Collapse>
                    </>
                  ) : (
                    <NavLink
                      to={data.to}
                      className={({ isActive }) =>
                        `custom-hover ${isActive ? 'active-menu-item' : ''}`
                      }
                      onClick={() => handleMenuActive(data.title, false)}
                    >
                      <i className={data.iconStyle} />
                      <span className="nav-text">{data.title}</span>
                    </NavLink>
                  )}
                </li>
              );
            })}
        </ul>
      </div>

      <div className="sidebar-footer copyright mt-0">
        <p>
          <strong>Fellowship Nomination System</strong> ©{' '}
          {new Date().getFullYear()} All Rights Reserved
        </p>
        <p className="fs-12">
          Made with
          <span
            ref={heartRef}
            className="heart"
            onClick={() => heartBlast()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') heartBlast();
            }}
            role="button"
            tabIndex="0"
            aria-label="Toggle heart animation"
          />{' '}
          by AAS
        </p>
      </div>
    </div>
  );
};

export default SideBar;
